<template>
  <div class="kv-editor">
    <!-- Object: one labeled row per key, plus a way to add a new one. -->
    <template v-if="isPlainObject">
      <div v-for="key in objectKeys" :key="key" class="kv-editor__row mb-2">
        <div class="d-flex align-items-center justify-content-between mb-1">
          <label class="mb-0">{{ humanize(key) }}</label>
          <el-button size="mini" type="text" icon="el-icon-delete" @click="removeKey(key)" />
        </div>
        <key-value-editor :value="value[key]" @input="(v) => setKey(key, v)" />
      </div>
      <div class="d-flex align-items-center mt-2 kv-editor__add-field">
        <el-input v-model="newKeyName" placeholder="New field name" size="mini" class="mr-2" />
        <el-button size="mini" @click="addKey">Add field</el-button>
      </div>
    </template>

    <!-- Array: one editor per item, each removable, plus add. -->
    <template v-else-if="isArray">
      <div v-for="(item, index) in value" :key="index" class="kv-editor__array-item mb-2 pb-2">
        <div class="d-flex align-items-center justify-content-between mb-1">
          <span class="small text-muted">Item {{ index + 1 }}</span>
          <el-button size="mini" type="danger" icon="el-icon-delete" @click="removeItem(index)" />
        </div>
        <key-value-editor :value="item" @input="(v) => setItem(index, v)" />
      </div>
      <el-button size="mini" @click="addItem">Add item</el-button>
    </template>

    <el-switch v-else-if="isBoolean" :value="value" @change="emitInput" />

    <el-input-number v-else-if="isNumber" :value="value" style="width: 100%" @change="emitInput" />

    <!-- String, null, or anything else not handled above — edited as text. -->
    <el-input
      v-else
      :type="isLongText ? 'textarea' : 'text'"
      :rows="isLongText ? 3 : undefined"
      :value="value === null || value === undefined ? '' : value"
      @input="emitInput"
    />
  </div>
</template>

<script>
/**
 * Recursive editor for an arbitrary JSON value — the fallback for any custom
 * page block type this screen has no dedicated form for. Renders objects as
 * labeled rows, arrays as repeatable items, and primitives as a plain input,
 * recursing into nested objects/arrays, so nothing in a block's own data is
 * ever locked to API-only edits just because its shape wasn't designed for.
 */
export default {
  name: "KeyValueEditor",
  components: {
    // Self-reference for recursion — a Vue 2 SFC does not resolve its own tag
    // by name automatically, this is the standard workaround.
    KeyValueEditor: () => import("./KeyValueEditor.vue"),
  },
  props: {
    value: {
      type: [Object, Array, String, Number, Boolean],
      default: null,
    },
  },
  data() {
    return { newKeyName: "" };
  },
  computed: {
    isArray() {
      return Array.isArray(this.value);
    },
    isPlainObject() {
      return null !== this.value && "object" === typeof this.value && !this.isArray;
    },
    isBoolean() {
      return "boolean" === typeof this.value;
    },
    isNumber() {
      return "number" === typeof this.value;
    },
    isLongText() {
      return "string" === typeof this.value && (this.value.length > 60 || this.value.includes("\n"));
    },
    objectKeys() {
      return this.isPlainObject ? Object.keys(this.value) : [];
    },
  },
  methods: {
    humanize(key) {
      return key.replace(/_/g, " ");
    },
    emitInput(value) {
      this.$emit("input", value);
    },
    setKey(key, value) {
      this.$emit("input", { ...this.value, [key]: value });
    },
    removeKey(key) {
      const copy = { ...this.value };
      delete copy[key];
      this.$emit("input", copy);
    },
    addKey() {
      const name = this.newKeyName.trim();
      if (!name || Object.prototype.hasOwnProperty.call(this.value, name)) {
        return;
      }
      this.$emit("input", { ...this.value, [name]: "" });
      this.newKeyName = "";
    },
    setItem(index, value) {
      const copy = this.value.slice();
      copy[index] = value;
      this.$emit("input", copy);
    },
    removeItem(index) {
      const copy = this.value.slice();
      copy.splice(index, 1);
      this.$emit("input", copy);
    },
    addItem() {
      const template = this.value.length ? this.cloneEmpty(this.value[0]) : "";
      this.$emit("input", [...this.value, template]);
    },
    cloneEmpty(sample) {
      if (Array.isArray(sample)) {
        return [];
      }
      if (null !== sample && "object" === typeof sample) {
        const out = {};
        Object.keys(sample).forEach((key) => {
          out[key] = this.cloneEmpty(sample[key]);
        });
        return out;
      }
      if ("boolean" === typeof sample) {
        return false;
      }
      if ("number" === typeof sample) {
        return 0;
      }
      return "";
    },
  },
};
</script>

<style scoped>
.kv-editor__row,
.kv-editor__array-item {
  border-left: 2px solid #ebeef5;
  padding-left: 10px;
}
.kv-editor__add-field {
  gap: 8px;
}
</style>
